import {useContext, useEffect, useMemo, useState} from "react";
import SectionHeader from "../../../util/component/SectionHeader";
import { useTranslation } from "react-i18next";
import ContentBody from "../common/content-body/ContentBody";
import {useForm} from "react-hook-form";
import {IServerSideDatasource} from "ag-grid-community";
import {SEARCH_APPLICATION_FILTERS_INITIAL_VALUES} from "./config";
import {filterObject} from "../utils";
import {searchApplication} from "./applicationSearch.actions";
import SearchToolFields from "./partials/SearchToolFields";
import SearchToolTable from "./partials/SearchToolTable";
import { ISortBy } from "../../booking/api/types";
import { deepClone } from "../../../util/state/commonFunctions";
import { useRecoilState } from "recoil";
import { appFilterParams } from "./state";
import { useMutation } from "@tanstack/react-query";
import CustomLoader from "../../../util/component/CustomLoader";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";

const SearchTool = () => {
    const {t} = useTranslation(['network', 'translation']);
    const [filterExpanded, setFilterExpanded] = useState(false);
    const [filterParams, setFilterParams] = useRecoilState(appFilterParams);
    const raSearchListMutation = useMutation(searchApplication)
    const {globalData} = useContext(GlobalContext);

    let filterParamsClone = deepClone(filterParams);

    const searchApplicationForm = useForm({
        defaultValues: SEARCH_APPLICATION_FILTERS_INITIAL_VALUES,
    });

    useEffect(() => {
        if (globalData.user.legalEntities.length === 1) {
            searchApplicationForm.setValue('tenants', globalData.user.legalEntities);
        } else if (globalData.user.legalEntities.length > 1) {
            searchApplicationForm.setValue('tenants', globalData.user.legalEntities.filter((elem: any) => elem.name !== 'all'));
            searchApplicationForm.setValue('dealers', globalData.user.dealers.filter((elem: any) => elem.name !== 'all'));
        }

        const data = searchApplicationForm.getValues();
        setFilterParams(filterObject(data, x => JSON.stringify(data[x]) !== JSON.stringify(SEARCH_APPLICATION_FILTERS_INITIAL_VALUES[x])));
    }, []);

    const handleSearch = (data: any) => {
        setFilterExpanded(false);
        setFilterParams(filterObject(data, x => JSON.stringify(data[x]) !== JSON.stringify(SEARCH_APPLICATION_FILTERS_INITIAL_VALUES[x])));
    };

    const serverSideDatasource: IServerSideDatasource = useMemo(() => ({
        getRows(params) {
            const sortBy: ISortBy | undefined = params.request.sortModel[0] ? {
                field: params.request.sortModel[0].colId,
                order: params.request.sortModel[0].sort
            } : undefined;
            const pagination = Number(params.request.endRow) - Number(params.request.startRow);

            if (!filterParamsClone) {
                filterParamsClone = {};
            }
            const {startRow: startIndex, endRow: endIndex} = params.request

            filterParamsClone['paramRequest'] = { pagination, sortBy, startIndex, endIndex };

            if (!!filterParamsClone.tenants) {
                const ind = filterParamsClone.tenants.findIndex((elem: any) => elem.name === 'all');
                if (ind > -1) {
                    filterParamsClone.tenants =
                        globalData.user.legalEntities
                            .filter((elem: any) => elem.name !== 'all')
                            .map((elem: any) => elem.name);
                } else {
                    filterParamsClone.tenants = filterParamsClone.tenants.map((elem: any) => elem.name);
                }
            }

            raSearchListMutation.mutateAsync(filterParamsClone)
            .then((response: any) => {
                params.success({
                    rowData: response.listApplication,
                    rowCount: response.params.totCount
                });
                response.params.totCount === 0 && params.api.showNoRowsOverlay();
            })
            .catch(() => {
                params.fail();
                params.api.hideOverlay();
            }) 
        }
    }), [filterParams]);

    return (
        <>
            <SectionHeader title={t('view.title')}/>
            <ContentBody>
                <SearchToolFields searchApplicationForm={searchApplicationForm} handleSearch={handleSearch} filterExpandedState={[filterExpanded, setFilterExpanded]} />
                <SearchToolTable serverSideDatasource={serverSideDatasource} />
            </ContentBody>
            <CustomLoader loading={raSearchListMutation.isLoading} />
        </>
    )
}

export default SearchTool;
